import React, { useState, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { listCampuses, createCampus } from '../../api/campusPortalApi';
import * as S from './Management.styles';

const CampusManagement = () => {
  const auth = useAuth();
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Get user's Cognito groups
  const userGroups = auth.user?.profile['cognito:groups'] || [];
  const portalGroups = userGroups.filter(g => g.startsWith('portal_'));

  // Permission checks based on Cognito groups
  const isDeveloper = portalGroups.includes('portal_developer');
  const isSystemAdmin = portalGroups.includes('portal_system_admin');
  const isDirector = portalGroups.includes('portal_director');
  const isManager = portalGroups.includes('portal_manager');
  const isAdmin = portalGroups.includes('portal_admin');
  const canViewCampuses = isDeveloper || isSystemAdmin || isDirector || isManager || isAdmin;
  const canCreateCampus = isDeveloper || isSystemAdmin || isDirector; // Developer, System Admin and Director can create campuses

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCampusName, setNewCampusName] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch campuses on mount
  useEffect(() => {
    if (!canViewCampuses) {
      setLoading(false);
      return;
    }

    fetchCampuses();
  }, [canViewCampuses]);

  const fetchCampuses = async () => {
    setLoading(true);
    setError(null);
    try {
      const fetchedCampuses = await listCampuses(true);
      setCampuses(fetchedCampuses);
    } catch (err) {
      console.error('Error fetching campuses:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredCampuses = campuses.filter(campus => {
    return campus.name?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleCreateCampus = async () => {
    if (!newCampusName.trim()) {
      alert('Please enter a campus name');
      return;
    }

    setActionLoading(true);
    try {
      const newCampus = await createCampus(newCampusName.trim());
      setCampuses([...campuses, newCampus]);
      setShowCreateModal(false);
      setNewCampusName('');
      alert('Campus created successfully!');
    } catch (err) {
      console.error('Error creating campus:', err);
      alert(`Failed to create campus: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Show loading while checking auth
  if (auth.isLoading || loading) {
    return (
      <S.Container>
        <S.Header>
          <div>
            <S.Title>Campus Management</S.Title>
            <S.Subtitle>Loading...</S.Subtitle>
          </div>
        </S.Header>
      </S.Container>
    );
  }

  // Show permission denied if user cannot view campuses
  if (!canViewCampuses && !loading) {
    return (
      <S.Container>
        <S.Header>
          <div>
            <S.Title>Campus Management</S.Title>
            <S.Subtitle>Access Restricted</S.Subtitle>
          </div>
        </S.Header>
        <S.ErrorBanner>
          You do not have permission to view campuses. Only System Admins, Directors, Managers, and Admins can access this page.
        </S.ErrorBanner>
      </S.Container>
    );
  }

  return (
    <S.Container>
      <S.Header>
        <div>
          <S.Title>Campus Management</S.Title>
          <S.Subtitle>Manage campus locations</S.Subtitle>
        </div>
        {canCreateCampus && (
          <S.Button color="#F1AF59" onClick={() => setShowCreateModal(true)}>
            + Create New Campus
          </S.Button>
        )}
      </S.Header>

      {error && (
        <S.ErrorBanner>
          Error loading campuses: {error}
        </S.ErrorBanner>
      )}

      <S.FiltersBar>
        <S.SearchInput
          type="text"
          placeholder="Search by campus name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </S.FiltersBar>

      <S.TableContainer>
        <S.Table>
          <thead>
            <tr>
              <S.Th>Campus Name</S.Th>
              <S.Th>Created Date</S.Th>
            </tr>
          </thead>
          <tbody>
            {filteredCampuses.length === 0 ? (
              <tr>
                <S.Td colSpan={2} style={{ textAlign: 'center', color: '#6b7280' }}>
                  {searchTerm ? 'No campuses match your search' : 'No campuses found'}
                </S.Td>
              </tr>
            ) : (
              filteredCampuses.map(campus => (
                <tr key={campus.id}>
                  <S.Td>
                    <S.UserName>{campus.name}</S.UserName>
                  </S.Td>
                  <S.Td>
                    {campus.createdAt ? new Date(campus.createdAt).toLocaleDateString() : '-'}
                  </S.Td>
                </tr>
              ))
            )}
          </tbody>
        </S.Table>
      </S.TableContainer>

      <S.Stats>
        Total Campuses: {campuses.length}
      </S.Stats>

      {/* Create Campus Modal */}
      {showCreateModal && (
        <S.ModalOverlay>
          <S.Modal>
            <S.ModalHeader>
              <h3>Create New Campus</h3>
              <S.CloseButton onClick={() => setShowCreateModal(false)}>&times;</S.CloseButton>
            </S.ModalHeader>
            <S.ModalContent>
              <S.FormGroup>
                <label>Campus Name</label>
                <S.Input
                  type="text"
                  placeholder="Enter campus name"
                  value={newCampusName}
                  onChange={(e) => setNewCampusName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !actionLoading) {
                      handleCreateCampus();
                    }
                  }}
                />
              </S.FormGroup>
            </S.ModalContent>
            <S.ModalFooter>
              <S.Button color="#6b7280" onClick={() => setShowCreateModal(false)}>
                Cancel
              </S.Button>
              <S.Button
                color="#F1AF59"
                onClick={handleCreateCampus}
                disabled={actionLoading}
              >
                {actionLoading ? 'Creating...' : 'Create Campus'}
              </S.Button>
            </S.ModalFooter>
          </S.Modal>
        </S.ModalOverlay>
      )}
    </S.Container>
  );
};

export default CampusManagement;
